import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSidebar, type AppShellSidebarGroup } from "@musa.tech/design-system";
import { postToHost, subscribeToHost } from "./bridge";
import { isEmbedMode } from "./host";
import { iconName } from "./iconNames";
import type { EmbedNavGroup } from "./protocol";

interface UseEmbedBridgeArgs {
  groups: AppShellSidebarGroup[];
  isNavigationLoading: boolean;
}

const serialize = (groups: AppShellSidebarGroup[]): EmbedNavGroup[] =>
  groups.map((group) => ({
    id: group.id,
    label: group.label,
    items: group.items.map((item) => ({
      id: item.id,
      label: item.label,
      icon: iconName(item.icon),
    })),
  }));

/**
 * Ponte com o host, no modo embed: manda menu e rota para cima, recebe ordem
 * de navegação de volta.
 *
 * O menu é montado aqui e enviado pronto porque quem sabe o que o usuário pode
 * ver é o token do Keycloak, que vive deste lado. O host renderiza o que
 * chega; se ele filtrasse por conta própria, seriam duas cópias da regra de
 * permissão para manter em sincronia — e a de fora ficaria desatualizada.
 *
 * Fora do modo embed, todas as chamadas são no-op (ver `bridge.ts`).
 */
export function useEmbedBridge({ groups, isNavigationLoading }: UseEmbedBridgeArgs): void {
  const navigate = useNavigate();
  const location = useLocation();
  const { open: sidebarOpen } = useSidebar();
  const lastNav = useRef<string | null>(null);

  useEffect(() => {
    postToHost({ type: "ready" });
  }, []);

  useEffect(
    () =>
      subscribeToHost((message) => {
        if (message.type !== "navigate") return;
        // Sem esta guarda, o eco fecha o ciclo: o host ordena a rota, o guest
        // avisa que chegou nela, o host reordena a mesma rota.
        if (message.path === window.location.pathname) return;
        navigate(message.path);
      }),
    [navigate],
  );

  useEffect(() => {
    // Fora do embed o postToHost já é no-op; a guarda evita serializar
    // o menu inteiro a cada mudança para jogar fora em seguida.
    if (isNavigationLoading || !isEmbedMode()) return;
    const payload = serialize(groups);
    const signature = JSON.stringify(payload);
    if (signature === lastNav.current) return;
    lastNav.current = signature;
    postToHost({ type: "nav", groups: payload });
  }, [groups, isNavigationLoading]);

  useEffect(() => {
    postToHost({ type: "route", path: location.pathname });
  }, [location.pathname]);

  /**
   * O botão de recolher menu fica no header, que continua sendo nosso, mas o
   * menu que ele recolheria é o do host. O `AppShellHeader` chama o
   * `toggleSidebar` do design-system direto, sem passar por aqui — então o que
   * dá para observar é o efeito: a sidebar local (que no embed nem é
   * renderizada) mudou de estado, logo alguém clicou no botão. O primeiro
   * valor é ignorado; só a mudança vira recado.
   */
  const previousSidebarOpen = useRef(sidebarOpen);
  useEffect(() => {
    if (previousSidebarOpen.current === sidebarOpen) return;
    previousSidebarOpen.current = sidebarOpen;
    postToHost({ type: "toggle-sidebar" });
  }, [sidebarOpen]);
}
